import React, { useState, useEffect } from 'react';
import { Grid, withStyles, Dialog, DialogTitle, Tooltip, IconButton } from "@material-ui/core";
import { connect } from 'react-redux';
import ExportForm from './ExportForm';
import { CloudDownload } from '@material-ui/icons';
import { download } from '../../sc-utilities/download.utils';
import { snackActions } from '../../sc-utilities/SnackBar';
import { hasPermission } from "../../sc-utilities";
import config from "../../../config/config";
import * as actions from "../../sc-dashboard/ProductOverview/store/productOverviewActions";
import * as groupActions from "../../sc-group/Group/store/groupActions";
import * as searchActions from '../../sc-dashboard/Search/store/searchAction';

const styles = theme => ({
    root: {
        width: '90%',
        height: 'auto',
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(2),
    },
    dialogPaper: {
        minHeight: '30vh',
        maxHeight: '70vh',
    },
    title: {
        marginTop: theme.spacing(2),
        textAlign: 'center'
    }
});

const Export = props => {
    const { exportProductData, classes, tableRef, formSettings, initialValues, route, translations, showVisibilities, showTypes, activeCustomerID, showProductOverviewExportTypes, showProductOverviewOutputMode, dateRangeValue, exportIconTooltip, tooltipValue, permissions, fetchGroups, groupsFromParent, fromSearchList, allProductIDs, preSelectAll } = props;
    const [isOpen, setIsOpen] = useState(false);

    const [groups, setGroups] = useState(null);

    let translateName = tooltipValue ?? route?.replace('-', '_') ?? '';

    useEffect(() => {
        const getGroups = async () => {
            const resultGroups = groupsFromParent ? { data: groupsFromParent } : await fetchGroups({ ActiveCustomerID: activeCustomerID });
            if (resultGroups) {
                let groupsToSet = resultGroups.data?.map(group => ({ value: group.GroupID, displayValue: group.Name }));
                if (hasPermission(permissions, 'access_all_collies_on_customer')) {
                    groupsToSet = [...groupsToSet, { value: 'n/a', displayValue: 'n/a' }];
                }
                setGroups(groupsToSet);
            }
        }

        if (hasPermission(permissions, 'group_list')) {
            getGroups();
        }
    }, [fetchGroups, activeCustomerID, permissions, groupsFromParent]);

    const handleDownload = async data => {
        const columnKeys = Object.keys(data).filter(key => !["GroupID", "Visibility", "Type", "FileType", "OutputModes", "Download", "Email", "includePDFReports", "includeDiagnoseReports", "includeVerificationReports", "includeAggregatedPDFErasureReports", "includeAggregatedPDFVerificationReports", "includeSummaryPDFErasureReports", "includeSummaryPDFVerificationReports", "Excel", "JSON", "XML", "CSV", "generateNew", "individualExcelReports", "individualJsonReports", "individualXmlReports", "individualCsvReports"].includes(key));
        const columns = [];
        for (let key of columnKeys) {
            if (data[key]) {
                columns.push(key);
            }
        }

        const exportFormatKeys = Object.keys(data).filter(key => ["Excel", "JSON", "XML", "CSV"].includes(key));
        const exportFormats = [];
        for (const key of exportFormatKeys) {
            if (data[key]) {
                const exportFormat = {};
                switch (key) {
                    case "Excel":
                        exportFormat.xlsx = { individualAssets: !!data?.individualExcelReports };
                        break;
                    case "JSON":
                        exportFormat.json = { individualAssets: !!data?.individualJsonReports };
                        break;
                    case "XML":
                        exportFormat.xml = { individualAssets: !!data?.individualXmlReports };
                        break;
                    case "CSV":
                        exportFormat.csv = { individualAssets: !!data?.individualCsvReports };
                        break;
                }
                exportFormats.push(exportFormat);
            }
        }

        if (data.GroupID === 'n/a') {
            data.GroupID = null;
        }

        let objectURL = `${config.backend_domain}:${config.backend_port}/${route}/export-${data.FileType}?ActiveCustomerID=${activeCustomerID}&Columns=${JSON.stringify(columns)}`;
        if (data.Visibility) {
            objectURL += `&Visibility=${data.Visibility}`;
        }
        if (data.Type) {
            objectURL += `&Type=${data.Type}`;
        }

        let outputModes = Object.keys(data).filter(key => ["Download", "Email"].includes(key)).filter((key) => data[key]);
        if (data.OutputModes && dateRangeValue) {
            let params = {
                ActiveCustomerID: activeCustomerID,
                CollectionType: 'custom_collection',
                ExportFormats: exportFormats,
                OutputModes: outputModes.map(ef => ef.toLowerCase()),
                includePDFReports: data.includePDFReports,
                includeAggregatedPDFErasureReports: data.includeAggregatedPDFErasureReports,
                includeAggregatedPDFVerificationReports: data.includeAggregatedPDFVerificationReports,
                includeSummaryPDFErasureReports: data.includeSummaryPDFErasureReports,
                includeSummaryPDFVerificationReports: data.includeSummaryPDFVerificationReports,
                includeDiagnoseReports: data.includeDiagnoseReports,
                includeVerificationReports: data.includeVerificationReports,
                GroupID: data.GroupID,
                generateNew: data.generateNew,
                Query: {
                    From: dateRangeValue.From.format('YYYY-MM-DD'),
                    To: dateRangeValue.To.format('YYYY-MM-DD'),
                    q: tableRef?.current?.state?.query?.q,
                    orders: tableRef?.current?.state?.query?.orders,
                },
            }
            await exportProductData(params);
        } else if (data.OutputModes && fromSearchList) {
            const params = {
                ActiveCustomerID: activeCustomerID,
                CollectionType: 'search_collection',
                ExportFormats: exportFormats,
                OutputModes: outputModes.map(ef => ef.toLowerCase()),
                includePDFReports: data.includePDFReports,
                includeAggregatedPDFErasureReports: data.includeAggregatedPDFErasureReports,
                includeAggregatedPDFVerificationReports: data.includeAggregatedPDFVerificationReports,
                includeSummaryPDFErasureReports: data.includeSummaryPDFErasureReports,
                includeSummaryPDFVerificationReports: data.includeSummaryPDFVerificationReports,
                includeDiagnoseReports: data.includeDiagnoseReports,
                includeVerificationReports: data.includeVerificationReports,
                GroupID: data.GroupID,
                generateNew: data.generateNew,
                ProductIDs: allProductIDs,
            }

            await exportProductData(params);
        } else {
            let exportDataSuccessful = await download(objectURL);
            if (!exportDataSuccessful) {
                return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
            }
        }
        tableRef.current && tableRef.current.onQueryChange()
        setIsOpen(false);
    };

    return (
        <>
            <Tooltip title={exportIconTooltip ? translations[`export_filtered_data`] || `Export filtered data` : translations[`download_${translateName}`] || `Download ${translateName.replace('_', ' ')} settings`}>
                <IconButton
                    id={`download_${translateName}`}
                    component={'div'}
                    aria-label={translations[`download_${translateName}`] || `Download ${translateName.replace('_', ' ')} settings`}
                    onClick={() => setIsOpen(true)}
                >
                    <CloudDownload />
                </IconButton>
            </Tooltip>
            <Dialog classes={{ paper: classes.dialogPaper }} onClose={() => setIsOpen(false)} aria-labelledby="customized-dialog-title" open={isOpen} fullWidth={true}
                maxWidth={'md'} >
                <DialogTitle className={classes.title}>{`Export ${translateName.replace('_', ' ')} data`} </DialogTitle>
                <Grid container >
                    <Grid item xs={12}>
                        <ExportForm
                            setIsOpen={setIsOpen}
                            initialValues={initialValues}
                            formSettings={formSettings}
                            showVisibilities={showVisibilities}
                            showTypes={showTypes}
                            translations={translations}
                            handleDownload={handleDownload}
                            showProductOverviewExportTypes={showProductOverviewExportTypes}
                            showProductOverviewOutputMode={showProductOverviewOutputMode}
                            preSelectAll={preSelectAll}
                            groups={groups}
                        />
                    </Grid>
                </Grid>
            </Dialog>
        </>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state?.translate?.translations?.activeTranslations || {},
        permissions: state?.core?.login?.permissions || [],
        allProductIDs: state?.statistic?.searches?.list?.allProductIDs || [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        fetchProductOverviewData: filter => dispatch(actions.fetchProductOverviewData(filter)),
        fetchProductOverviewDataCount: filter => dispatch(actions.fetchProductOverviewDataCount(filter)),
        exportProductData: params => dispatch(actions.exportProductData(params)),
        fetchGroups: params => dispatch(groupActions.fetchGroups(params)),
        exportSearchData: params => dispatch(searchActions.exportSearchData(params)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(Export));
